import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { validateBookingForm } from '../../utils/validationUtils';
import { formatDate, formatTime, formatDuration } from '../../utils/dateUtils';

/**
 * Booking form component for collecting customer details
 */
const BookingForm = ({
  selectedSlot,
  meetingType,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
  userProfile = null,
  rebookData = null,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Pre-populate form with user profile or rebook data
  useEffect(() => {
    if (rebookData) {
      // Rebook: use original booking data
      setFormData({
        name: rebookData.customerName || '',
        email: rebookData.customerEmail || '',
        phone: rebookData.customerPhone || '',
        notes: rebookData.notes || '',
      });
    } else if (userProfile) {
      // New booking: use user profile data
      const fullName = [userProfile.firstName, userProfile.lastName]
        .filter(Boolean)
        .join(' ');
      setFormData({
        name: fullName || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        notes: '',
      });
    }
  }, [userProfile, rebookData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validation = validateBookingForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    onSubmit(formData);
  };

  const slotStart = selectedSlot ? new Date(selectedSlot.slot_start || selectedSlot.start) : null;
  const slotEnd = selectedSlot ? new Date(selectedSlot.slot_end || selectedSlot.end) : null;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
      >
        {t('booking.yourDetails')}
      </Typography>

      {/* Booking summary */}
      {selectedSlot && (
        <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography 
            variant="subtitle2" 
            color="primary" 
            gutterBottom
            sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
          >
            {t('booking.bookingDetails')}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
            <strong>{t('booking.service')}:</strong> {meetingType?.description || meetingType?.name || 'Appointment'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
            <strong>{t('booking.date')}:</strong> {formatDate(slotStart, 'EEEE, MMMM d, yyyy')}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
            <strong>{t('booking.time')}:</strong> {formatTime(slotStart, 'HH:mm')} - {formatTime(slotEnd, 'HH:mm')}
          </Typography>
          {meetingType?.duration && (
            <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              <strong>{t('booking.duration')}:</strong> {formatDuration(parseInt(meetingType.duration))}
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {userProfile && (!formData.name || !formData.email || !formData.phone) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('booking.incompleteProfile') || 'Your profile information is incomplete. Please contact support.'}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          name="name"
          label={t('booking.name')}
          value={formData.name}
          onChange={handleChange}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
          fullWidth
          required
          margin="normal"
          disabled={!!userProfile}
        />

        <TextField
          name="email"
          label={t('booking.email')}
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          fullWidth
          required
          margin="normal"
          disabled={!!userProfile}
        />

        <TextField
          name="phone"
          label={t('booking.phone')}
          value={formData.phone}
          onChange={handleChange}
          error={!!validationErrors.phone}
          helperText={validationErrors.phone}
          fullWidth
          required
          margin="normal"
          disabled={!!userProfile}
        />

        <TextField
          name="notes"
          label={t('booking.notes')}
          value={formData.notes}
          onChange={handleChange}
          multiline
          rows={3}
          fullWidth
          margin="normal"
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          >
            {t('booking.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          >
            {loading ? t('common.loading') : t('booking.confirm')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default BookingForm;
